import DuoMascot from '@/components/v2/DuoMascot'
import DuoSpeechBubble from '@/components/v2/DuoSpeechBubble'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Duolingo's word bank: tiles are tapped in order to build the sentence, and
 * tapping a placed tile sends it back. Unlike the recognition drills there is
 * no correct option to spot, which is why it is reserved for intermediate and
 * advanced — it is the exercise that makes those lessons look harder as well as
 * read harder.
 *
 * The bank is the sentence plus decoys, ordered so the sentence cannot be built
 * by tapping left to right.
 */
export default function WordBankExercise({
  bank,
  locked,
  onChange,
  prompt,
}: {
  bank: string[]
  locked: boolean
  /** The sentence as built so far, for the parent to check. */
  onChange: (tokens: string[]) => void
  /** The sentence to translate, in the learner's own language. */
  prompt: string
}) {
  // Indices rather than strings: a bank can hold the same token twice, and
  // comparing by value would take the wrong tile back out.
  const [placed, setPlaced] = useState<number[]>([])

  const { t } = useTranslation()

  // Reported from an effect rather than from the handlers so both updates below
  // can stay functional. Computing the next array from `placed` directly loses
  // a tile when two tiles are tapped inside one frame, which on a word bank is
  // just someone typing a sentence at a normal speed.
  useEffect(() => {
    onChange(placed.map((index) => bank[index]))
  }, [bank, onChange, placed])

  const place = (index: number) => {
    if (locked) return

    setPlaced((previous) =>
      previous.includes(index) ? previous : [...previous, index],
    )
  }

  const take = (index: number) => {
    if (locked) return

    setPlaced((previous) => previous.filter((other) => other !== index))
  }

  return (
    <div className="flex grow flex-col">
      <h1 className="duo-heading">
        {t('pages.onboarding.v2.lesson.wordBankTitle')}
      </h1>

      <div className="mt-6 flex items-start gap-3">
        <DuoMascot className="h-14 w-14 shrink-0" />

        <div className="pt-1">
          <DuoSpeechBubble tail="left">
            <p className="text-duo-ink text-lg font-bold">{prompt}</p>
          </DuoSpeechBubble>
        </div>
      </div>

      {/*
        The answer line keeps its height whether or not anything is in it, so
        the bank below does not jump up and down as tiles move between the two.
      */}
      <div className="border-duo-line mt-8 min-h-16 border-y-2 py-3">
        <div className="flex flex-wrap gap-2">
          {placed.map((index) => (
            <button
              className="border-duo-line text-duo-ink rounded-xl border-2 bg-white px-3 py-2 text-base font-bold"
              key={index}
              onClick={() => take(index)}
              type="button"
            >
              {bank[index]}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {bank.map((token, index) => {
          const isPlaced = placed.includes(index)

          return (
            <button
              // Kept in the layout once placed, so the bank does not reflow
              // under the thumb mid-sentence.
              className={
                isPlaced
                  ? 'border-duo-line text-duo-line rounded-xl border-2 bg-transparent px-3 py-2 text-base font-bold'
                  : 'border-duo-line text-duo-ink rounded-xl border-2 border-b-4 bg-white px-3 py-2 text-base font-bold active:mt-0.5 active:border-b-2'
              }
              disabled={isPlaced || locked}
              key={`${token}-${index}`}
              onClick={() => place(index)}
              type="button"
            >
              {token}
            </button>
          )
        })}
      </div>
    </div>
  )
}
